/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import {
    createObservableDataAction,
    IAction,
    IActionContext,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';

import { CategoriesInput } from '@msdyn365-commerce-modules/retail-actions';
// import { } from './dobbies-get-categories-hierarchy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { CacheType } from '@msdyn365-commerce/retail-proxy';
import { getCategoryHierarchyAction, CategoriesInput as RawCategoriesInput } from './get-categories-hierarchy.override.action';
/**
 * Action Input Class for get-current-category-hierarchy.
 */
export class CurrentCategoryHierarchyInput extends CategoriesInput {
    public activeCategoryId: number;

    public constructor(context: IRequestContext, mappedToHierarchy: boolean, maxItems?: number, activeCategoryId?: number) {
        super(context, mappedToHierarchy, maxItems);
        const defaultActiveCategoryId = 0;
        this.activeCategoryId = activeCategoryId ?? defaultActiveCategoryId;
    }

    // @ts-expect-error: Overriding cache object type
    public getCacheObjectType = () => 'CurrentCategoryHierarchy';

    public getCacheKey = () => `${this.activeCategoryId}-${this.channelId}`;

    // 'request' caches the result for the duration of the request so other actions reuse it without a new API call.
    public dataCacheType = (): CacheType => 'request';
}

/**
 * Creates the input required for the data action.
 * @param inputData
 */
export const createCategoriesHierarchyInput = (inputData: ICreateActionContext<IGeneric<IAny>>): CurrentCategoryHierarchyInput => {
    const topItems = inputData.config?.topCategories && Number.parseInt(inputData.config.topCategories, 10);
    let activeCategoryId =
        inputData.requestContext.urlTokens.pageType === 'Category' ? inputData.requestContext.urlTokens.itemId : undefined;
    activeCategoryId = activeCategoryId ?? inputData.requestContext.query?.categoryId;
    const activeCategoryParsedId = activeCategoryId ? Number.parseInt(activeCategoryId, 10) : undefined;

    return new CurrentCategoryHierarchyInput(inputData.requestContext, true, topItems, activeCategoryParsedId);
};

/**
 * Finds the given category in the list of categories and returns an array with the info on where it's located.
 * @param categories - Categories list to search from.
 * @param categoryId - The category id to find.
 * @returns A list of categories starting from the given category id, ending up the root parent.
 */
const findCategoryHierarchy = (categories: CategoryHierarchy[], categoryId: number): CategoryHierarchy[] | null => {
    for (const category of categories) {
        if (category.RecordId === categoryId) {
            return [category];
        }

        if (ArrayExtensions.hasElements(category.Children)) {
            const innerCategoryHierarchy = findCategoryHierarchy(category.Children, categoryId);
            if (ArrayExtensions.hasElements(innerCategoryHierarchy)) {
                innerCategoryHierarchy.push(category);
                return innerCategoryHierarchy;
            }
        }
    }

    return null;
};

/**
 * Get the hierarchy of categories that are currently active as a parent -> child array.
 * Return will be as follows: [ParentCategory, Child, SubChild, SubSubChild].
 * @param input
 * @param ctx
 */
export async function getCurrentCategoryHierarchyAction(
    input: CurrentCategoryHierarchyInput,
    ctx: IActionContext
): Promise<CategoryHierarchy[]> {
    // try {
    //const fullCategoryHierarchy = await getCategoriesHierarchy(new CategoriesInput(ctx.requestContext, true, input.maxItems), ctx);
    // const fullCategoryHierarchy = await getCategoriesHierarchy(new CategoriesInput(ctx.requestContext, true, input.maxItems), ctx);
    const fullCategoryHierarchyWithCallableAction = await getCategoryHierarchyAction(
        new RawCategoriesInput(ctx.requestContext, true, input.maxItems),
        ctx
    );
    const categoryArray = findCategoryHierarchy(fullCategoryHierarchyWithCallableAction, input.activeCategoryId) ?? [];
    const sortedCategoryArray = categoryArray.reverse();
    return sortedCategoryArray;
}

export const getCurrentCategoryHierarchyActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-current-category-hierarchy',
    action: getCurrentCategoryHierarchyAction as IAction<CategoryHierarchy[]>,
    input: createCategoriesHierarchyInput
});

export default getCurrentCategoryHierarchyActionDataAction;
